import { buildProps, definePropType } from '@ll-plus/utils'

import type { TooltipProps } from '@ll-plus/components'
import type {
  FormProps as AntFormProps,
  ColProps,
  FormItemProps,
  RowProps,
} from 'ant-design-vue'
import type { ExtractPropTypes } from 'vue'
import type Form from '../form.vue'

type FormType<T = any> = (
  | 'input'
  | 'input-number'
  | 'cascader'
  | 'checkbox'
  | 'checkbox-group'
  | 'code-editor'
  | 'date-picker'
  | 'time-picker'
  | 'date-range'
  | 'time-range'
  | 'radio'
  | 'radio-group'
  | 'select'
  | 'switch'
  | 'textarea'
  | 'upload-image'
) &
  T

export interface FormChangeEvent {
  item: FormColumn
  value: any
  formData: FormData
}
export interface BasicFormColumn extends FormItemProps {
  label?: string
  key: string
  spanCol?: number | 24 // 一行几条
  colOptions?: ColProps
  tip?: string
  info?: string
  tipConfig?: TooltipProps
  type: FormType
  unit?: string
  change?: () => void
  rowOptions?: RowProps
  children?: FormColumn[]
  [key: string]: any
}

export type FormColumn<T = any> = BasicFormColumn & T

export type FormData = Record<string, any>

export const formProps = buildProps({
  /**
   * @description 表单项列表
   */
  formColumns: {
    type: definePropType<FormColumn[]>(Array),
    default: () => [],
  },
  /**
   * @description 展开的行变化时触发
   */
  formOptions: {
    type: definePropType<AntFormProps>(Object),
    default: () => ({
      layout: 'horizontal',
    }),
  },
  /**
   * @description 展开的行变化时触发
   */
  formData: {
    type: definePropType<FormData>(Object),
    default: () => ({}),
  },
  /**
   * @description 展开的行变化时触发
   */
  rowOptions: {
    type: definePropType<RowProps>(Object),
    default: () => ({
      gutter: 16,
    }),
  },
  /**
   * @description 表单加载状态
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 自动聚焦
   */
  autoFocus: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否全体禁用
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 当某一规则校验不通过时，是否停止剩下的规则的校验
   */
  validateErrorStop: {
    type: Boolean,
    default: true,
  },
} as const)

export const formEmits = {
  /**
   * @description 表单项值改变时触发
   */
  change: (params: FormChangeEvent) => !!params,
  blur: (params: FormChangeEvent) => !!params,
}

// props
export type FormProps = ExtractPropTypes<typeof formProps>

// emits
export type FormEmits = typeof formEmits

// instance
export type FormInstance = InstanceType<typeof Form>
